import { Button, Card, CardContent, Grid, Stack, CardActions, Typography, CircularProgress } from "@mui/material";
import GroupsIcon from '@mui/icons-material/Groups';
import { StepHeader, stepCardSx, stepActionsSx, backBtnSx, nextBtnSx } from "./stepUi";
import { useSelector, useDispatch } from "react-redux";
import { setActiveNext, setActiveBack } from "../campaignSlice";
import { useEffect, useState, useMemo } from "react";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import { setCampaignTags } from "../campaignSlice";
import { getCampaignTagUsersThunk, getCampaignInputParamsThunk } from "../campaignThunk";
import CircularLoader from "../../../components/loaders/circularLoader";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";


export default function IncludeExcludeTags() {
    const { activeStep, campaignTagsDetails, tagWiseUsers, status, error } = useSelector((state) => state.campaigns);

    const { getTags } = useSelector((state) => state.contacts);

    const dispatch = useDispatch();

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [state, setState] = useState({
        selectedIncludeTag: [],
        selectedExcludeTag: [],
        errors: {}
    });


    const { selectedIncludeTag, selectedExcludeTag, errors } = state;

    const handleMultiSelectChange = (name, selectedValues) => {
        const safeSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];

        setState((prev) => ({
            ...prev,
            [name]: selectedValues,
            errors: { ...prev.errors, [name]: "" },
        }));


        const includeTags =
            name === "selectedIncludeTag"
                ? safeSelectedValues.map((item) => item.value)
                : (selectedIncludeTag || []).map((item) => item.value);

        const excludeTags =
            name === "selectedExcludeTag"
                ? safeSelectedValues.map((item) => item.value)
                : (selectedExcludeTag || []).map((item) => item.value);

        // Prepare payload
        const payLoad = {
            includeTags: includeTags.join(","),
            excludeTags: excludeTags.join(","),
        };

        // Dispatch only if either list has values
        if (includeTags.length > 0 || excludeTags.length > 0) {
            dispatch(getCampaignTagUsersThunk({ credentials: payLoad }));
        } else {
            dispatch(getCampaignTagUsersThunk({ credentials: payLoad }));
        }
    };


    const columns = useMemo(() => [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        { accessorKey: "name", header: "Name", },
        { accessorKey: "email", header: "Email" },
        { accessorKey: "mobile", header: "Mobile" },
    ], []);

    const filterTags = (include, exclude) => {
        return include.filter((tag) => !exclude.some((excludeTag) => excludeTag.value === tag.value));
    };

    const availableIncludeTags = filterTags(getTags.map(item => ({ label: item.tag_name, value: item.id })), selectedExcludeTag);
    const availableExcludeTags = filterTags(getTags.map(item => ({ label: item.tag_name, value: item.id })), selectedIncludeTag);

    const campaignFields = [
        { id: 1, name: "selectedIncludeTag", options: availableIncludeTags, value: selectedIncludeTag, label: "Include Tags" },
        { id: 2, name: "selectedExcludeTag", options: availableExcludeTags, value: selectedExcludeTag, label: "Exclude Tags", disabled: selectedIncludeTag.length === 0 ? true : false },
    ];

    const validateStep = () => {
        const validations = [
            { valid: selectedIncludeTag.length > 0, message: "Include is required", field: "selectedIncludeTag" },
        ];

        const newErrors = {};
        let isValid = true;

        for (let rule of validations) {
            if (!rule.valid) {
                setSnackbar((prev) => ({
                    ...prev,
                    open: true,
                    message: rule.message,
                    severity: "error",
                }));

                setState((prev) => ({
                    ...prev,
                    errors: { ...prev.errors, [rule.field]: rule.message },
                }));

                isValid = false;
                return;
            }
        }
        setState((prev) => ({ ...prev, errors: { ...prev.errors, ...newErrors } }));
        return isValid;
    };


    const handleBack = () => {
        dispatch(setCampaignTags([{
            includeTags: selectedIncludeTag,
            excludeTags: selectedExcludeTag
        }]));
        dispatch(setActiveBack());
    };


    const handleNext = () => {
        if (activeStep === 1) {
            if (!validateStep()) return;
            dispatch(setCampaignTags([{
                includeTags: selectedIncludeTag,
                excludeTags: selectedExcludeTag
            }]));
        }
        dispatch(getCampaignInputParamsThunk());
        dispatch(setActiveNext());
    };

    useEffect(() => {
        if (campaignTagsDetails?.length > 0) {
            const { includeTags, excludeTags } = campaignTagsDetails[0];
            setState((prev) => ({
                ...prev,
                selectedIncludeTag: includeTags,
                selectedExcludeTag: excludeTags,
            }));


            const payLoad = {
                includeTags: includeTags.map((item) => item.value).toString(""),
                excludeTags: excludeTags.map((item) => item.value).toString(""),
            };

            // Dispatch only if either list has values
            if (includeTags.length > 0 || excludeTags.length > 0) {
                dispatch(getCampaignTagUsersThunk({ credentials: payLoad }));
            }
        }
    }, [campaignTagsDetails]);

    // 🔹 Snackbar for errors
    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    const renderUserCount = () => {
        return (
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ px: 1.5, py: 0.75, minHeight: 36, width: "fit-content", maxWidth: "100%", borderRadius: { xs: 3, sm: 5 }, bgcolor: "#19875412", border: "1px solid #19875433", color: "#198754" }}>
                <GroupsIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={800}>{tagWiseUsers.length}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: "#475569" }}>contacts will receive this campaign</Typography>
            </Stack>
        )
    }


    return (
        <Card sx={stepCardSx}>

            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <StepHeader icon={<GroupsIcon sx={{ fontSize: 20 }} />} title="Choose Audience" subtitle="Include contacts by tag, and exclude tags you don't want to message" />
                <Grid container spacing={2} sx={{ mb: 1 }}>
                    {
                        campaignFields.map((field, inex) => (
                            <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={field.name}>
                                <FilterMultiSelect
                                    name={field.name}
                                    label={field.label}
                                    options={field.options}
                                    value={state[field.name] || []}
                                    onChange={handleMultiSelectChange}
                                    formHelperText={false}
                                    error={!!errors[field.name]}
                                    disabled={field.disabled}
                                />
                            </Grid>
                        ))
                    }
                </Grid>


                {
                    tagWiseUsers.length > 0 ? (
                        <DataTableWithCheckbox
                            columns={columns}
                            data={tagWiseUsers || []}
                            renderFilters={renderUserCount()}
                            status={status}
                            enableRowSelection={false}
                            searchFirst
                        />

                    ) : ("")
                }

            </CardContent>

            <CardActions sx={stepActionsSx}>
                <Stack direction="row" spacing={1.5}>
                    <Button disabled={activeStep === 0} variant="outlined" onClick={handleBack} sx={backBtnSx}>
                        Back
                    </Button>
                    {/* <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={status !== "succeeded"}
                        sx={{ ...styles, background: "var(--primary)", color: "var(--light-bg)", }}
                    >
                        Next
                    </Button> */}

                    <div style={{ cursor: status === "loading" ? "not-allowed" : "pointer" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={status === "loading"}
                            onClick={status === "loading" ? null : handleNext}
                            sx={nextBtnSx}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 14.5,
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {status === "loading" && (
                                    <CircularProgress
                                        size={15}
                                        sx={{ mr: 1, color: "var(--white)" }}  // ✅ force white spinner
                                    />
                                )}
                                {status === "loading" ? "Loading.." : "Next"}
                            </Typography>
                        </Button>
                    </div>
                </Stack>
            </CardActions>
            <SnackbarMessage open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} />
        </Card>
    )
};